import { useCallback, useEffect, useMemo, useState } from "react";
import { cms } from "../../api/cms.service";
import type { CmsMenu, CmsPage } from "../../types/cms";
import { buildSidebarOptions } from "./pageSidebar";
import type { CmsPagesStatus } from "./useCmsPages";

export type SidebarOptionsStatus = "loading" | "ready" | "failed";

type MenusStatus = SidebarOptionsStatus;

function combinedStatus(
  menusStatus: MenusStatus,
  pagesStatus: CmsPagesStatus,
): SidebarOptionsStatus {
  if (menusStatus === "failed" || pagesStatus === "failed") {
    return "failed";
  }
  if (menusStatus === "ready" && pagesStatus === "ready") {
    return "ready";
  }
  return "loading";
}

export function useSidebarOptions(pages: CmsPage[], pagesStatus: CmsPagesStatus) {
  const [rootMenus, setRootMenus] = useState<CmsMenu[]>([]);
  const [rootMenusStatus, setRootMenusStatus] = useState<MenusStatus>("loading");

  const reloadSidebarOptions = useCallback(async () => {
    try {
      const { menus } = await cms.getMenus({ parentId: null });
      setRootMenus(menus);
      setRootMenusStatus("ready");
    } catch (err) {
      console.error("[useSidebarOptions] failed to load sidebars", err);
      setRootMenusStatus("failed");
    }
  }, []);

  useEffect(() => {
    reloadSidebarOptions();
  }, [reloadSidebarOptions]);

  const sidebarOptions = useMemo(() => buildSidebarOptions(rootMenus, pages), [rootMenus, pages]);

  return {
    sidebarOptions,
    sidebarOptionsStatus: combinedStatus(rootMenusStatus, pagesStatus),
    reloadSidebarOptions,
  };
}
